import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardService } from '../services/dashboard.service';
import type {
  DashboardSummary,
  DashboardStatistics,
  StatisticsPeriod,
} from '../types/dashboard.types';
import { SummaryCard } from '../components/dashboard/SummaryCard';
import { ActivityChart } from '../components/dashboard/ActivityChart';
import { ReviewBreakdownCard } from '../components/dashboard/ReviewBreakdownCard';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { Button } from '../components/common/Button';

const TrophyIcon = (): React.ReactElement => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0" />
  </svg>
);

const ChartBarIcon = (): React.ReactElement => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
  </svg>
);

const FlameIcon = (): React.ReactElement => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1A3.75 3.75 0 0012 18z" />
  </svg>
);

const CheckIcon = (): React.ReactElement => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export function StatisticsPage(): React.ReactElement {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [statistics, setStatistics] = useState<DashboardStatistics | null>(null);
  const [period, setPeriod] = useState<StatisticsPeriod>('7d');

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [sumData, statData] = await Promise.all([
        dashboardService.getDashboardSummary(),
        dashboardService.getDashboardStatistics(period),
      ]);
      setSummary(sumData);
      setStatistics(statData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load statistics.');
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  const handlePeriodChange = async (newPeriod: StatisticsPeriod) => {
    setPeriod(newPeriod);
    setIsLoadingStats(true);
    try {
      const statData = await dashboardService.getDashboardStatistics(newPeriod);
      setStatistics(statData);
    } catch (err) {
      console.error('Failed to change period:', err);
    } finally {
      setIsLoadingStats(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (isLoading) {
    return (
      <div className="py-28 flex justify-center">
        <LoadingSpinner size="lg" label="Loading detailed statistics..." />
      </div>
    );
  }

  if (error || !summary || !statistics) {
    return (
      <div className="space-y-6 animate-slide-in">
        <ErrorMessage
          message={error || 'Failed to initialize statistics.'}
          onRetry={fetchStats}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Learning Analytics & Statistics</h1>
          <p className="text-sm text-slate-400 mt-1">
            Detailed retention trends, review accuracy, and performance breakdowns over time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/dashboard')}>
            &larr; Back to Dashboard
          </Button>
          <Button variant="primary" onClick={() => navigate('/review')}>
            Review Session
          </Button>
        </div>
      </div>

      {/* Top Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          id="stat-accuracy"
          label="Overall Accuracy"
          value={`${summary.reviewAccuracy}%`}
          subtitle="Good + Easy reviews ratio"
          icon={<TrophyIcon />}
          variant="emerald"
        />
        <SummaryCard
          id="stat-retention"
          label="Retention Rate"
          value={`${summary.retentionRate}%`}
          subtitle={`${summary.learnedVocabulary} of ${summary.totalVocabulary} words`}
          icon={<CheckIcon />}
          variant="primary"
        />
        <SummaryCard
          id="stat-total-reviews"
          label="Total Reviews"
          value={summary.totalReviews}
          subtitle={`${summary.reviewsToday} completed today`}
          icon={<FlameIcon />}
          variant="amber"
        />
        <SummaryCard
          id="stat-period-reviews"
          label={`Reviews (${period})`}
          value={statistics.totalReviewsInPeriod}
          subtitle={`${statistics.reviewAccuracy}% accuracy in period`}
          icon={<ChartBarIcon />}
          variant="sky"
        />
      </div>

      {/* Activity Chart */}
      <ActivityChart
        dailyActivity={statistics.dailyActivity}
        period={period}
        onPeriodChange={handlePeriodChange}
        isLoading={isLoadingStats}
      />

      {/* Review Breakdown */}
      <ReviewBreakdownCard
        breakdown={statistics.reviewBreakdown}
        accuracy={statistics.reviewAccuracy}
      />
    </div>
  );
}
